import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import type { FoodicsUnitSource } from "src/types/foodics";
import { ImportModeCard } from "../import-mode-card";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ScaleIcon from "@mui/icons-material/Scale";
import Inventory2Icon from "@mui/icons-material/Inventory2";

type InventoryImportModalProps = {
  open: boolean;
  handleClose: () => void;
  isPending: boolean;
  onConfirm: (unitSource: FoodicsUnitSource) => void;
};

/**
 * Quick-import "Import inventory" question: Foodics tracks two units per item
 * (ingredient + storage) but Jazr stores one. This modal picks a single unit
 * source that applies to every item in the direct import.
 */
export const InventoryImportModal = ({
  open,
  handleClose,
  isPending,
  onConfirm,
}: InventoryImportModalProps) => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const [unitSource, setUnitSource] = useState<FoodicsUnitSource>("ingredient");
  const isArabic = i18n.language === "ar";

  const onClose = () => {
    if (isPending) return;
    setUnitSource("ingredient");
    handleClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography fontSize={18} fontWeight={600} color="#101828">
          {t("foodicsWorkspace.inventoryImport.unitQuestion")}
        </Typography>
        <Typography fontSize={14} color="#667085">
          {t("foodicsWorkspace.inventoryImport.unitHint")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <ImportModeCard
            icon={<ScaleIcon fontSize="small" />}
            iconBgcolor="#FF8D3F"
            iconColor="#FFFFFF"
            title={t("foodicsWorkspace.inventoryImport.ingredientTitle")}
            subtitle={t("foodicsWorkspace.inventoryImport.ingredientSubtitle")}
            isSelected={unitSource === "ingredient"}
            onSelect={() => setUnitSource("ingredient")}
          />
          <ImportModeCard
            icon={<Inventory2Icon fontSize="small" />}
            iconBgcolor="#F2F4F7"
            iconColor="#667085"
            title={t("foodicsWorkspace.inventoryImport.storageTitle")}
            subtitle={t("foodicsWorkspace.inventoryImport.storageSubtitle")}
            isSelected={unitSource === "storage"}
            onSelect={() => setUnitSource("storage")}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {generalT("cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={isPending}
          endIcon={
            isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ArrowForwardIcon
                sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
                fontSize="small"
              />
            )
          }
          onClick={() => onConfirm(unitSource)}
        >
          {t("foodicsWorkspace.importInventory")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
